// src/utils/auth.js
import { safeGet, safeRemove } from './safeStorage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { clearMyPermissionsCache } from '../hooks/useMyPermissions';

/**
 * Check if a JWT token is expired.
 * @param {string} token - JWT token
 * @returns {boolean} - true if expired/invalid, false if still valid
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

/**
 * Check if the user is authenticated and the token is still valid.
 *
 * Uses safeStorage so hardened / private-mode browsers (Falkon, Safari private)
 * that expose `localStorage` as null or throw on access do not crash the
 * component tree on first render.
 */
export const isAuthenticated = () => {
  const token = safeGet(STORAGE_KEYS.authToken);
  const user = safeGet(STORAGE_KEYS.user);
  if (!token || !user) return false;

  if (isTokenExpired(token)) {
    console.log('Token is expired, clearing authentication data');
    clearAuthData();
    return false;
  }
  return true;
};

export const clearAuthData = () => {
  safeRemove(STORAGE_KEYS.authToken);
  safeRemove(STORAGE_KEYS.user);
  safeRemove(STORAGE_KEYS.selectedGST);
  safeRemove(STORAGE_KEYS.lastActivity);
  safeRemove(STORAGE_KEYS.lastTokenRefresh);
  // Permissions are cached per page load; drop them with the identity they belong to,
  // rather than relying on the post-login hard reload to reset module state.
  clearMyPermissionsCache();
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEYS.edpmsReconciliation);
    }
  } catch (_err) {
    /* ignore private-mode sessionStorage */
  }
};

export const getAuthToken = () => safeGet(STORAGE_KEYS.authToken);
